/**
 * Where login, signup and reactivation all end.
 *
 * Reactivation is a login; it must not have its own way of becoming one.
 */

import { setTokens } from "../../lib/api/tokenStore";
import CONSUMER_SERVICES from "../profile/consumerService";
import {
  EMPTY_PROFILE,
  mergeIdentity,
  toUserProfile,
} from "../profile/consumerMappers";
import { setProfile } from "../profile/profileStore";

const settledData = (result) =>
  result.status === "fulfilled" ? result.value?.data ?? null : null;

/**
 * Stores the tokens, then reads both records and merges them.
 *
 * Order is load-bearing: tokens go in FIRST because both reads need the
 * bearer. And `allSettled`, not `all` — either read failing must not block
 * login. If only the identity read answers, it merges over EMPTY_PROFILE and
 * a returning user with a half-answered backend still gets in.
 *
 * @param {{authToken: string, refreshToken: string}} tokens
 * @returns {Promise<object>} the merged UserProfile
 */
export const completeAuth = async (tokens) => {
  setTokens({
    authToken: tokens.authToken,
    refreshToken: tokens.refreshToken,
  });

  return hydrateProfile();
};

/**
 * The two reads on their own, for callers whose tokens are already stored —
 * Add Photo, and any cold start that restores a session.
 */
export const hydrateProfile = async () => {
  const [consumer, identity] = await Promise.allSettled([
    CONSUMER_SERVICES.fetchSelf(),
    CONSUMER_SERVICES.fetchIdentity(),
  ]);

  const consumerRow = settledData(consumer);
  const identityRow = settledData(identity);

  const base = consumerRow ? toUserProfile(consumerRow) : EMPTY_PROFILE;

  return setProfile(mergeIdentity(base, identityRow));
};

export default completeAuth;
